/**
 * Conges Dashboard Feature - Type Definitions
 */

/**
 * Leave request status codes from API
 * Backend EtatDemande enum (EtatDemandeFixture): SOUMISE, EN_VALIDATION,
 * APPROUVEE, REFUSEE, ANNULEE — rendered via the shared StatusBadge with the
 * rh/conges canonical STATUS config.
 */
export type LeaveStatusCode = "SOUMISE" | "EN_VALIDATION" | "APPROUVEE" | "REFUSEE" | "ANNULEE";

/**
 * Leave request from the API
 */
export interface LeaveRequest {
  [key: string]: unknown;
  /** Unique identifier */
  id: number;
  /** Leave type label */
  type: string;
  /** Leave type code */
  typeCode: string;
  /** Status label */
  etat: string;
  /** Status code */
  etatCode: LeaveStatusCode;
  /** Start date (ISO format with time) */
  dateDebut: string;
  /** End date (ISO format with time) */
  dateFin: string;
  /** Number of days */
  nombreJours: number;
  /** Whether the absence covers full working days */
  isJourneeComplete: boolean;
  /** Whether marked as urgent */
  isUrgent: boolean;
  /** Creation timestamp */
  dateCreation?: string;
}

/**
 * Pending approval item for managers/HR
 */
export interface PendingApproval {
  /** Unique identifier */
  id: number;
  /** Employee information */
  employee: {
    /** Last name */
    nom: string;
    /** First name */
    prenom: string;
  };
  /** Leave type label */
  type: string;
  /** Leave type code */
  typeCode: string;
  /** Start date (ISO format with time) */
  dateDebut: string;
  /** End date (ISO format with time) */
  dateFin: string;
  /** Number of days */
  nombreJours: number;
  /** Whether the absence covers full working days */
  isJourneeComplete: boolean;
  /** Whether marked as urgent */
  isUrgent: boolean;
}

/**
 * Team absence item for team calendar view
 */
export interface TeamAbsence {
  /** Unique identifier */
  id: number;
  /** Employee information */
  employee: {
    /** Last name */
    nom: string;
    /** First name */
    prenom: string;
  };
  /** Leave type label */
  type: string;
  /** End date (ISO format) */
  dateFin: string;
  /** Number of days */
  nombreJours: number;
}

/**
 * One paid-vacation (CP) campaign balance, year grain.
 * The dashboard renders one card per campaign (N, then N-1).
 */
export interface CampagneCp {
  /** Campaign start year (e.g. 2026 for 01/06/2026 → 31/05/2027) */
  annee: number;
  periodeDebut: string;
  periodeFin: string;
  /** Days acquired in the campaign */
  total: number;
  /** Days used in the campaign */
  utilises: number;
  /** Remaining days in the campaign */
  solde: number;
}

/** API Platform wraps array-typed DTO properties in a Collection envelope */
export interface CampagnesCpCollection {
  member: CampagneCp[];
  totalItems: number;
}

/** Unwrap the API Platform Collection envelope (or passthrough a plain array). */
export function getCampagnesCp(data: CongesDashboardSummary | undefined): CampagneCp[] {
  const raw = data?.campagnesCp;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (raw && typeof raw === "object" && Array.isArray((raw as CampagnesCpCollection).member)) {
    return (raw as CampagnesCpCollection).member;
  }
  return [];
}

/**
 * Complete conges dashboard summary data
 * Combines all leave-related data for the dashboard view
 */
export interface CongesDashboardSummary {
  /** Current user's personne ID (nullable for users without personne) */
  personneId: number | null;
  /** When this data was generated */
  generatedAt: string | null;
  /** Paid vacation (CP) remaining balance */
  soldeCongesPayes: number;
  /** Paid vacation total allocation for the year */
  totalCongesPayes: number;
  /** Paid vacation reference year */
  anneeCongesPayes: number;
  /** Paid vacation days already used */
  utilisesCongesPayes: number;
  /**
   * Per-campaign balances (N first, then N-1). Empty when no campaign is open
   * yet — the pooled fields above are then rendered as a single card.
   */
  campagnesCp?: CampagneCp[] | CampagnesCpCollection | null;
  /** RTT remaining balance */
  soldeRTT: number;
  /** RTT total allocation for the year */
  totalRTT: number;
  /** RTT reference year */
  anneeRTT: number;
  /** RTT days already used */
  utilisesRTT: number;
  /** Heures supplémentaires total acquired in the current year (minutes) */
  totalHeuresSup: number;
  /** Heures supplémentaires minutes already used (recup/paiement, current year) */
  utilisesHeuresSup: number;
  /** Heures supplémentaires remaining balance (minutes) */
  soldeHeuresSup: number;
  /** Recent leave requests - can be array or API Platform Collection */
  recentDemandes: LeaveRequest[] | { member: LeaveRequest[]; totalItems: number } | null;
  /** Next upcoming leave */
  prochainConge: {
    id: number;
    type: string;
    dateDebut: string;
    dateFin: string;
    nombreJours: number;
    isJourneeComplete: boolean;
  } | null;
  /** Count of pending approvals for this user */
  pendingApprovalsCount: number;
  /** Pending approval items (for managers/HR) */
  pendingApprovals: PendingApproval[];
  /** Count of team absences today */
  teamAbsencesCount: number;
  /** Team absences for today */
  teamAbsencesToday: TeamAbsence[];
}
